import { useState } from 'react';
import { API_BASE } from '../config';
import { useI18n } from '../context/I18nContext';

export default function SessionFeedbackModal({ isOpen, sessionId, onSubmitted, onSkip }) {
  const { t } = useI18n();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (rating < 1 || rating > 5) {
      setError(t('feedbackRatingRequired') || 'Veuillez sélectionner une note.');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('cogency_token');
      const res = await fetch(`${API_BASE}/api/session-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ sessionId, rating, feedback, suggestions })
      });
      const data = await res.json();
      if (data.success) {
        setRating(0);
        setFeedback('');
        setSuggestions('');
        onSubmitted?.();
      } else {
        setError(data.error || t('feedbackSubmitError') || 'Erreur lors de l\'envoi.');
      }
    } catch (err) {
      setError(t('feedbackSubmitError') || 'Erreur réseau. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    setRating(0);
    setFeedback('');
    setSuggestions('');
    setError('');
    onSkip?.();
  };

  return (
    <div className="modal-overlay show" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div className="modal" style={{ background: 'var(--bg-card)', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '520px', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
        <h3 style={{ margin: '0 0 0.5rem' }}>⭐ {t('sessionFeedbackTitle') || 'Votre avis compte'}</h3>
        <p style={{ fontSize: '0.95rem', lineHeight: 1.6, marginBottom: '1rem', color: 'var(--text-secondary)' }}>
          {t('sessionFeedbackSubtitle') || 'Avant de quitter, aidez-nous à améliorer Cogency en partageant votre ressenti.'}
        </p>

        {error && <div style={{ color: '#ef4444', marginBottom: '0.75rem', fontSize: '0.9rem' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 500 }}>
              {t('feedbackRatingLabel') || 'Notez votre expérience'}
            </label>
            <div style={{ display: 'flex', gap: '8px', fontSize: '2rem' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    color: (hoverRating ? star <= hoverRating : star <= rating) ? '#fbbf24' : 'var(--text-muted)',
                    transition: 'color 0.15s'
                  }}
                  aria-label={`${star} étoile${star > 1 ? 's' : ''}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 500 }}>
              {t('feedbackExperienceLabel') || 'Qu\'avez-vous pensé de cette session ?'}
            </label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t('feedbackExperiencePlaceholder') || 'Votre retour d\'expérience...'}
              rows={3}
              style={{
                width: '100%',
                padding: '0.6rem 0.75rem',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--bg-input, var(--bg-secondary))',
                color: 'var(--text-primary)',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: 500 }}>
              {t('feedbackSuggestionsLabel') || 'Comment pouvons-nous nous améliorer ?'}
            </label>
            <textarea
              value={suggestions}
              onChange={(e) => setSuggestions(e.target.value)}
              placeholder={t('feedbackSuggestionsPlaceholder') || 'Vos suggestions...'}
              rows={3}
              style={{
                width: '100%',
                padding: '0.6rem 0.75rem',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--bg-input, var(--bg-secondary))',
                color: 'var(--text-primary)',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button type="button" className="btn-ghost btn-small" onClick={handleSkip} disabled={loading}>
              {t('skip') || 'Ignorer'}
            </button>
            <button type="submit" className="btn-primary btn-small" disabled={loading}>
              {loading ? (t('sending') || 'Envoi...') : (t('sendFeedback') || 'Envoyer mon avis')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
